import { type Msg, type Session } from '@halo/chat-sdk-react';
import { ItemWithExpireAnimate } from '@halo/ui-chat-react';
import { useIsMyMsg } from '@/scripts/hooks';
import { useStaticAppStore, useStaticSessionStore } from '@/store/client';

export interface MsgExpireAnimateProps {
  session: Session;
  message: Msg;
  children: React.ReactNode;
}

export const MsgExpireAnimate = memo(function MsgExpireAnimate({
  session,
  message,
  children,
}: MsgExpireAnimateProps) {
  const isMyMessage = useIsMyMsg(message);
  const [doExpireAnimation, setDoExpireAnimation] = useState(false);
  const { client } = useStaticAppStore();

  const sessionStore = useStaticSessionStore(session.chat_id);

  const onExpireAnimationEnd = useCallback(async () => {
    await sessionStore?.deleteMessages([message.cli_msg_id], false);
  }, [message.cli_msg_id, sessionStore]);

  useEffect(() => {
    if (client) {
      // ClearMessages
      const clearMessagesHandler = client.addEventListener('ClearMessages', ({ detail }) => {
        const { chat_id } = detail!;
        if (chat_id === session.chat_id) {
          setDoExpireAnimation(true);
        }
      });
      // DeleteMessages
      const deleteMessagesHandler = client.addEventListener('DeleteMessages', ({ detail }) => {
        const { chat_id, cli_msg_ids } = detail!;
        if (chat_id === session.chat_id && cli_msg_ids.includes(message.cli_msg_id)) {
          setDoExpireAnimation(true);
        }
      });
      return () => {
        clearMessagesHandler();
        deleteMessagesHandler();
      };
    }
  }, [message.cli_msg_id, client, session.chat_id]);

  return (
    <ItemWithExpireAnimate
      animateDirection={isMyMessage ? 'left' : 'right'}
      doExpireAnimation={doExpireAnimation}
      onExpireAnimationEnd={onExpireAnimationEnd}
    >
      {children}
    </ItemWithExpireAnimate>
  );
});
